import { EnsuredSubtype } from '@client/new/helpers/types';
import { isAfter, isSameDay, set } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { NewDiscount } from './newDiscount.viewModel';

export const camelCaseToTitleCase = (v: string) =>
  v
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());

export type ScheduleData = EnsuredSubtype<
  NewDiscount,
  | 'expiration'
  | 'expirationHour'
  | 'expirationMinute'
  | 'expirationTimezone'
  | 'expirationDayTime'
>;

// TODO: [G,J]: bring more generality into this function so it can be reused
export const createExpirationDate = (p: ScheduleData) => {
  let hours = p.expirationHour;
  if (p.expirationDayTime === 'PM' && hours !== 12) hours += 12;
  else if (p.expirationDayTime === 'AM' && hours === 12) hours = 0;

  const baseDate = new Date(p.expiration);

  const adjustedDate = set(baseDate, {
    hours,
    minutes: p.expirationMinute,
    seconds: 0,
    milliseconds: 0,
  });

  return zonedTimeToUtc(adjustedDate, p.expirationTimezone);
};

export const isAfterOrSameDay = (
  expirationDate: Date | number,
  startDate: Date | number,
) => isAfter(expirationDate, startDate) || isSameDay(expirationDate, startDate);

export function isDateWithinYears(
  expirationDate: Date | number,
  yearsAhead: number,
): boolean {
  const dateToCheck =
    typeof expirationDate === 'number'
      ? new Date(expirationDate)
      : expirationDate;

  const currentDate = new Date();
  const futureDate = new Date(
    currentDate.setFullYear(currentDate.getFullYear() + yearsAhead),
  );

  return dateToCheck <= futureDate;
}

export function formatNumberWithPrecision(
  value: number,
  precision: number,
): number {
  const factor = Math.pow(10, precision);
  return Math.round(value * factor) / factor;
}

export const ensureRange = (
  value: number,
  [min, max]: [number, number],
  precision = 2,
) => {
  const preciseValue = formatNumberWithPrecision(value, precision);
  const preciseMin = formatNumberWithPrecision(min, precision);
  const preciseMax = formatNumberWithPrecision(max, precision);

  if (preciseValue < preciseMin) return preciseMin;
  if (preciseValue > preciseMax) return preciseMax;
  return preciseValue;
};

//TODO [g,j]: type FormatDateWithTimeParams = { date: Date | number; prefix?: string; timeZone?: string; }
export const formatDateWithTime = (
  date: Date | number,
  prefix = '',
  timeZone = 'UTC',
) => {
  const zonedDate: Date = utcToZonedTime(date, timeZone);

  let hours = zonedDate.getHours();
  const minutes = String(zonedDate.getMinutes()).padStart(2, '0');

  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;

  return `${formatDateOnly(
    date,
    prefix,
    timeZone,
  )}, ${hours}:${minutes} ${ampm}`;
};

export const formatDateOnly = (
  date: Date | number,
  prefix = '',
  timeZone?: string,
) => {
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const zonedDate: Date = utcToZonedTime(date, tz);
  const year = zonedDate.getFullYear();
  const month = String(zonedDate.getMonth() + 1).padStart(2, '0');
  const day = String(zonedDate.getDate()).padStart(2, '0');

  const formattedDate = `${month}-${day}-${year}`;

  return `${prefix}${formattedDate}`;
};

export const toHourDate = (
  p: Pick<
    NewDiscount,
    'expirationHour' | 'expirationMinute' | 'expirationDayTime'
  >,
) => {
  if (p.expirationHour === undefined) return null;
  let hour = p.expirationHour;
  if (p.expirationMinute === undefined) return null;
  const minute = p.expirationMinute;
  const dt = p.expirationDayTime;
  if (!dt) return null;
  if (dt === 'PM' && hour !== 12) hour += 12;
  else if (dt === 'AM' && hour === 12) hour = 0;

  const expirationDate = new Date();
  expirationDate.setHours(hour);
  expirationDate.setMinutes(minute);
  expirationDate.setSeconds(0);
  return expirationDate;
};

export const anonymize = (input: string) =>
  `${input || ''}`.replace(/[^ ]/g, '*');
